/**
 * SolarComparisonChart Component
 *
 * Multi-series line chart: Fixed tariff, Agile (passive switch),
 * Agile+Solar (solar self-consumption, no export), and — when a
 * battery is combined — Agile+Solar+Battery (perfect-foresight DP).
 *
 * A green range-area fills the band between Agile and the best
 * solar-including scenario to visualise the solar/battery saving
 * on top of the tariff switch.
 *
 * Data source: useSolarSimulation hook → solar-simulate edge function.
 * No-export model: PV beyond load (and optional battery) is curtailed.
 */

import { useMemo, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import TariffSelector from '@/components/energy/TariffSelector'
import {
  identifyPreset as identifyBatteryPreset,
  getPreset as getBatteryPreset,
  getCheapestBatteryPreset,
  CUSTOM_PRESET_ID as CUSTOM_BATTERY_PRESET_ID,
} from '@/lib/batteryPresets'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

const FIXED_COLOR = '#f59e0b'           // amber
const AGILE_COLOR = '#0ea5e9'           // sky blue
const AGILE_SOLAR_COLOR = '#eab308'     // yellow — solar-only
const COMBO_COLOR = '#16a34a'           // green — solar + battery
const SAVING_COLOR = '#22c55e'          // green band

function formatMonth(month) {
  return new Date(month).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

function formatCurrency(value) {
  if (value == null) return '—'
  if (Math.abs(value) >= 1000) return `£${(value / 1000).toFixed(1)}k`
  return `£${value.toFixed(0)}`
}

function formatPencePerKwh(value) {
  if (value == null) return '—'
  return `${value.toFixed(1)}p`
}

function Row({ color, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm mt-1 first:mt-0">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-neutral-600">{label}</span>
      </div>
      <span className="font-medium text-neutral-900">{value}</span>
    </div>
  )
}

function CostTooltip({ active, payload, label, hasBattery, solarLabel, comboLabel }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })

  const best = hasBattery ? row.combo : row.solar
  const saving = row.agile - best
  const savingLabel = saving >= 0
    ? `£${saving.toFixed(0)} saved vs Agile passive`
    : `£${Math.abs(saving).toFixed(0)} worse than Agile passive`

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-52">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <Row color={FIXED_COLOR} label="Fixed" value={`£${row.fixed.toFixed(0)}`} />
      <Row color={AGILE_COLOR} label="Agile" value={`£${row.agile.toFixed(0)}`} />
      <Row color={AGILE_SOLAR_COLOR} label={solarLabel} value={`£${row.solar.toFixed(0)}`} />
      {hasBattery && (
        <Row color={COMBO_COLOR} label={comboLabel} value={`£${row.combo.toFixed(0)}`} />
      )}
      <div className="border-t border-neutral-200 mt-2 pt-2 text-sm">
        <span className="text-green-600">{savingLabel}</span>
      </div>
      {row.pv_generated != null && (
        <p className="text-xs text-neutral-500 mt-2">
          PV: {row.pv_generated.toFixed(0)} kWh generated ·{' '}
          {row.pv_self_consumed.toFixed(0)} self-consumed
          {row.pv_wasted > 0 && ` · ${row.pv_wasted.toFixed(0)} curtailed`}
        </p>
      )}
      {row.coverage_pct != null && row.coverage_pct < 100 && (
        <p className="text-xs text-neutral-500 mt-1">
          Coverage: {row.coverage_pct.toFixed(1)}%
        </p>
      )}
    </div>
  )
}

function UnitRateTooltip({ active, payload, label, hasBattery, solarLabel, comboLabel }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  const monthYear = new Date(label).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric'
  })
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-52">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      <Row color={FIXED_COLOR} label="Fixed" value={`${row.fixed.toFixed(1)}p/kWh`} />
      <Row color={AGILE_COLOR} label="Agile" value={`${row.agile.toFixed(1)}p/kWh`} />
      <Row color={AGILE_SOLAR_COLOR} label={solarLabel} value={`${row.solar.toFixed(1)}p/kWh`} />
      {hasBattery && (
        <Row color={COMBO_COLOR} label={comboLabel} value={`${row.combo.toFixed(1)}p/kWh`} />
      )}
      <p className="text-[10px] text-neutral-500 mt-2">
        Effective unit rate = total cost ÷ site load (standing charges included,
        solar generation not netted against denominator).
      </p>
    </div>
  )
}

export default function SolarComparisonChart({ data, isLoading, vatRate = 0, tariff, onTariffChange }) {
  const [mode, setMode] = useState('cost') // 'cost' | 'unit'

  const hasBattery = !!data?.aggregate?.total_agile_solar_battery_pence

  // Rated kWp label (e.g. "Agile + Solar 25.0kWp") — pulled from the
  // params the edge function echoed back so it always reflects what was
  // actually simulated, not whatever's currently staged in the form.
  const kwpLabel = useMemo(() => {
    const s = data?.params?.solar
    if (!s || !Number.isFinite(s.panel_watts) || !Number.isFinite(s.num_panels)) return null
    const kwp = (s.panel_watts * s.num_panels) / 1000
    return `${kwp.toFixed(1)}kWp`
  }, [data])

  // Battery capacity label — prefer the rated_kwh from a matched preset
  // (marketing-friendly, matches the dropdown), else fall back to the
  // simulator's usable capacity. When no battery was simulated, fall
  // back to the cheapest-per-kWh preset so the "+ Battery" label on the
  // chart still reads sensibly if consumers want to reference it.
  const batteryKwhLabel = useMemo(() => {
    const b = data?.params?.battery
    if (b) {
      const id = identifyBatteryPreset(b)
      if (id !== CUSTOM_BATTERY_PRESET_ID) {
        const preset = getBatteryPreset(id)
        if (preset) return `${preset.rated_kwh}kWh`
      }
      if (Number.isFinite(b.capacity_kwh_usable)) {
        return `${b.capacity_kwh_usable.toFixed(1)}kWh`
      }
    }
    const cheapest = getCheapestBatteryPreset()
    return cheapest ? `${cheapest.rated_kwh}kWh` : null
  }, [data])

  const solarLabel = kwpLabel ? `Agile + Solar ${kwpLabel}` : 'Agile + Solar'
  const batteryLabel = batteryKwhLabel ? `Battery ${batteryKwhLabel}` : 'Battery'
  const comboLabel = kwpLabel
    ? `Agile + Solar ${kwpLabel} + ${batteryLabel}`
    : `Agile + Solar + ${batteryLabel}`

  const chartData = useMemo(() => {
    if (!data?.monthly?.length) return []
    const costMult = 1 + (vatRate ?? 0)
    return data.monthly.map(row => {
      const kwh = parseFloat(row.kwh_consumed ?? 0) || 0
      const fixedCost = ((row.fixed_total_pence ?? 0) / 100) * costMult
      const agileCost = ((row.agile_total_pence ?? 0) / 100) * costMult
      const solarCost = ((row.agile_solar_total_pence ?? 0) / 100) * costMult
      const comboCost = row.agile_solar_battery_total_pence != null
        ? ((row.agile_solar_battery_total_pence) / 100) * costMult
        : null

      const fixedUnit = (row.fixed_total_pence ?? 0) / 100
      const agileUnit = (row.agile_total_pence ?? 0) / 100
      const solarUnit = (row.agile_solar_total_pence ?? 0) / 100
      const comboUnit = row.agile_solar_battery_total_pence != null
        ? row.agile_solar_battery_total_pence / 100
        : null

      const fixed = mode === 'cost' ? fixedCost : (kwh > 0 ? (fixedUnit * 100) / kwh : 0)
      const agile = mode === 'cost' ? agileCost : (kwh > 0 ? (agileUnit * 100) / kwh : 0)
      const solar = mode === 'cost' ? solarCost : (kwh > 0 ? (solarUnit * 100) / kwh : 0)
      const combo = comboCost != null
        ? (mode === 'cost' ? comboCost : (kwh > 0 ? (comboUnit * 100) / kwh : 0))
        : null

      const best = combo != null ? combo : solar
      const savingBand = best < agile ? [best, agile] : null

      return {
        month: row.month,
        fixed,
        agile,
        solar,
        combo,
        savingBand,
        coverage_pct: parseFloat(row.coverage_pct ?? 0),
        pv_generated: row.solar_kwh_generated,
        pv_self_consumed: row.solar_kwh_self_consumed,
        pv_wasted: row.solar_kwh_wasted,
      }
    })
  }, [data, mode, vatRate])

  const title = hasBattery
    ? `Fixed vs Agile vs ${solarLabel} vs ${comboLabel}`
    : `Fixed vs Agile vs ${solarLabel}`

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[360px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[360px] text-neutral-500">
            No simulation data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const isCost = mode === 'cost'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            {onTariffChange && (
              <TariffSelector value={tariff} onChange={onTariffChange} />
            )}
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(value) => value && setMode(value)}
            size="sm"
          >
            <ToggleGroupItem value="cost" className="text-xs px-3">
              Cost
            </ToggleGroupItem>
            <ToggleGroupItem value="unit" className="text-xs px-3">
              p/kWh
            </ToggleGroupItem>
          </ToggleGroup>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          No-export model: PV beyond load{hasBattery && ' (and battery)'} is curtailed.{' '}
          {hasBattery && 'Battery dispatch is perfect-foresight DP — a ceiling on achievable savings. '}
          {mode === 'cost'
            ? 'Costs shown inc VAT.'
            : 'Unit rates shown excl VAT (standing charge amortised over kWh consumed).'}
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={isCost ? formatCurrency : formatPencePerKwh}
            />
            <Tooltip content={
              isCost
                ? <CostTooltip hasBattery={hasBattery} solarLabel={solarLabel} comboLabel={comboLabel} />
                : <UnitRateTooltip hasBattery={hasBattery} solarLabel={solarLabel} comboLabel={comboLabel} />
            } />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
              iconType="line"
            />
            <Area
              type="monotone"
              dataKey="savingBand"
              stroke="none"
              fill={SAVING_COLOR}
              fillOpacity={0.18}
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="fixed"
              name="Fixed"
              stroke={FIXED_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="agile"
              name="Agile"
              stroke={AGILE_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="solar"
              name={solarLabel}
              stroke={AGILE_SOLAR_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {hasBattery && (
              <Line
                type="monotone"
                dataKey="combo"
                name={comboLabel}
                stroke={COMBO_COLOR}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
